<style lang="scss" scoped>
.shopping-basket-full {
    .not-empty {
        h1 {
            @apply text-32 font-black;

            @media screen and (max-width: 1023.98px) {
                font-size: 1.5rem !important;
                line-height: 1.9375rem !important;
            }
        }
    }

    .title {
        width: 270px;

        @media screen and (min-width: 768px) {
            width: 592px;
        }

        @media screen and (min-width: 1024px) {
            width: 358px;
        }

        @media screen and (min-width: 1280px) {
            width: 334px;
        }

        @media screen and (min-width: 1920px) {
            width: 384px;
        }
    }

    .close {
        position: absolute;
        width: 12px;
        height: 12px;
        right: 10px;
        top: 10px;
        cursor: pointer;

        &:before,
        &:after {
            content: '';
            position: absolute;
            width: 14px;
            height: 1px;
            border-radius: 1px;
            left: 0;
            transform-origin: left;
            transition: background 0.15s ease-in-out;
            background-color: #000;
        }

        &:before {
            top: 1px;
            transform: rotate(45deg);
        }

        &:after {
            bottom: 0;
            transform: rotate(-45deg);
        }

        &:hover {
            &:before,
            &:after {
                background-color: #000;
            }
        }
    }

    .decrease {
        height: 52px;
        width: 32px;
        border: 1px solid #aeb2bb;
        border-right-width: 0;
        border-radius: 4px 0 0 4px;
        background-color: #ffffff;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        font-size: 0.875rem;
        transition: color 0.2s;

        @media screen and (min-width: 1024px) {
            width: 52px;
        }
    }

    .add-amount {
        height: 52px;
        width: 40px;
        border: 1px solid #aeb2bb;
        border-right-width: 0;
        border-left-width: 0;
        border-radius: 0;
        padding: 19px 8px 18px 8px;

        &:focus {
            outline: none;
        }

        @media screen and (min-width: 1024px) {
            width: 52px;
        }
    }

    .increase {
        height: 52px;
        width: 32px;
        border: 1px solid #aeb2bb;
        border-left-width: 0;
        border-radius: 0 4px 4px 0;
        background-color: #ffffff;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        font-size: 0.875rem;
        transition: color 0.2s;

        @media screen and (min-width: 1024px) {
            width: 52px;
        }
    }
}

.component-fade-enter-active,
.component-fade-leave-active {
    transition: opacity 0.3s ease;
}
.component-fade-enter,
.component-fade-leave-to {
    opacity: 0;
}
</style>

<template>
    <section class="shopping-basket-full">
        <div class="row flex justify-center">
            <transition-group name="component-fade" mode="out-in" tag="div" class="xl:w-10/12 relative w-full">
                <div class="lg:mt-120 left-1/2 absolute top-0 flex items-center justify-center flex-1 mt-40 transform -translate-x-1/2" key="loading" v-if="!ready">
                    <spinner></spinner>
                </div>
                <div class="not-empty" v-else-if="this.basket.products.length > 0" key="not-empty">
                    <div class="flex flex-col items-stretch justify-between flex-1">
                        <h1 class="mb-24 text-gray-800">{{ translations.shopping_basket_title }}</h1>

                        <transition name="component-fade" mode="out-in">
                            <div v-if="bannerOpen && $store.getters['isReseller']" class="rounded-xs relative p-24 mb-24 bg-gray-100">
                                <div class="close" @click="bannerOpen = false"></div>
                                <p class="text-20 mb-8 font-bold">{{ translations.reseller_discount }}</p>
                                <p v-html="translations.reseller_discount_description.replace(/\{{(.+?)\}}/g, $store.getters['percentage'])"></p>
                            </div>
                        </transition>

                        <div class="relative">
                            <div class="last:pb-0 last:mb-0 last:border-transparent flex flex-wrap pb-24 mb-24 border-b border-gray-300" v-for="product in this.basket.products" :key="product.id">
                                <div class="lg:w-1/2 flex w-full" :class="{ 'opacity-50': product.amount > getAvailableVariantStock(product.variant) }">
                                    <img class="w-60 h-60 md:w-80 md:h-80 rounded-xs object-cover object-center mr-16" :src="getFirstImage(product.productData)" :alt="product.productData.name" />
                                    <div class="lg:py-8 lg:pr-16 flex flex-col flex-1">
                                        <p class="text-20 whitespace-nowrap title overflow-ellipsis min-w-0 mb-8 overflow-hidden font-bold leading-6 text-gray-800" v-html="product.productData.name"></p>
                                        <div class="flex items-center">
                                            <template v-for="option in product.productData.options.filter((o) => o.type !== 'opp_calc')">
                                                <span class="text-14 font-medium leading-tight text-gray-500" :key="option.key">{{ product.variant.content[option.key] }}</span>
                                                <span class="text-10 mx-8 font-medium text-gray-500" :key="`${option.key}_bull`">•</span>
                                            </template>
                                            <a :href="`/product/${product.productData.slug}`" class="text-14 hover:border-transparent font-medium leading-tight text-gray-500 transition-colors border-b border-gray-500">{{ translations.view_product }}</a>
                                        </div>
                                    </div>
                                </div>
                                <div class="lg:w-1/2 lg:pt-0 flex items-start justify-between w-full pt-16">
                                    <div class="flex items-center">
                                        <div class="lg:mr-56 lg:pt-8 mr-32" style="width: 140px" v-if="getAvailableVariantStock(product.variant) === 0">
                                            <p class="text-red">Niet voorradig</p>
                                        </div>
                                        <div class="lg:mr-40 flex flex-col mr-16" v-else>
                                            <div class="lg:pt-8 flex">
                                                <a href="#" class="decrease" :class="{ 'text-gray-200 cursor-default': product.amount <= 1 }" @click.prevent="decreaseAmount(product)" v-html="svg('minus')"></a>
                                                <input :value="product.amount" @change="updateAmount($event, product)" type="number" class="add-amount text-center appearance-none" />
                                                <a href="#" class="increase" :class="{ 'text-gray-200 cursor-default': product.amount >= getAvailableVariantStock(product.variant) }" @click.prevent="increaseAmount(product)" v-html="svg('plus')"></a>
                                            </div>
                                            <p class="text-red text-14 mt-8" v-if="product.amount > getAvailableVariantStock(product.variant)">Slechts {{ getAvailableVariantStock(product.variant) }} op voorraad</p>
                                            <p class="text-red text-14 mt-8" v-else-if="product.amount < product.variant.min_order_amount">Minimum {{ product.variant.min_order_amount }}</p>
                                        </div>

                                        <a href="#" @click.prevent="removeProduct(product.variant.id)" class="text-14 hover:border-transparent font-medium leading-tight text-gray-500 transition-colors border-b border-gray-500">{{ translations.remove }}</a>
                                    </div>
                                    <div class="lg:pt-8 flex flex-col items-end flex-shrink-0 text-right" :class="{ 'opacity-50 pointer-events-none line-through': product.amount > getAvailableVariantStock(product.variant) }">
                                        <span class="text-20 mb-8 font-bold text-gray-800">&euro; {{ formatCurrency(getVariantPrice(product, true) * product.amount) }}</span>
                                        <span class="text-14 font-medium leading-tight text-gray-500">&euro; {{ formatCurrency(getVariantPrice(product, false) * product.amount) }} excl. btw</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="border-dark flex flex-row justify-end pt-40 mt-40 border-t">
                            <div class="lg:w-5/12 lg:w-1/2 w-full">
                                <div class="flex justify-between mb-8">
                                    <span class="text-black">{{ translations.subtotal }}</span>
                                    <span class="text-gray-500">&euro; {{ subTotal }}</span>
                                </div>
                                <div class="flex justify-between mb-8">
                                    <span class="text-black">{{ translations.vat }}</span>
                                    <span class="text-gray-500">&euro; {{ vat }}</span>
                                </div>
                                <div class="flex justify-between mb-24">
                                    <span class="text-black">{{ translations.shipping }}</span>
                                    <span class="text-gray-500">Volgende stap</span>
                                </div>
                                <div class="flex justify-between mb-24">
                                    <span class="text-20 font-bold text-black">{{ translations.total }}</span>
                                    <span class="text-gray-500">&euro; {{ totalPrice }}</span>
                                </div>
                                <div class="flex items-center mb-24">
                                    <tooltip v-if="$store.getters['basketErrors'].length && $store.getters['basketErrors'].find((e) => ['out_of_stock', 'not_enough_stock'].includes(e.issue))" :text="translations.shopping_basket_stock_error" class="w-full">
                                        <div class="btn btn-rounded w-full text-center text-white border-none pointer-events-none" style="background-color: rgba(35, 179, 47, 0.6)">{{ translations.submit_button }}<span class="bg-red text-12 inline-flex items-center justify-center w-16 h-16 ml-16 font-bold text-white rounded-full">!</span></div>
                                    </tooltip>
                                    <tooltip v-else-if="$store.getters['basketErrors'].length && $store.getters['basketErrors'].find((e) => e.issue === 'order_amount_too_low')" :text="translations.order_amount_too_low.replace(/\{{(.+?)\}}/g, $store.getters['customer'].min_order_amount)" class="w-full">
                                        <div class="btn btn-rounded w-full text-center text-white border-none pointer-events-none" style="background-color: rgba(35, 179, 47, 0.6)">{{ translations.submit_button }}<span class="bg-red text-12 inline-flex items-center justify-center w-16 h-16 ml-16 font-bold text-white rounded-full">!</span></div>
                                    </tooltip>
                                    <tooltip v-else-if="$store.getters['basketErrors'].length && $store.getters['basketErrors'].find((e) => e.issue === 'min_order_amount')" :text="translations.min_order_amount_too_low.replace(/\{{(.+?)\}}/g)" class="w-full">
                                        <div class="btn btn-rounded w-full text-center text-white border-none pointer-events-none" style="background-color: rgba(35, 179, 47, 0.6)">{{ translations.submit_button }}<span class="bg-red text-12 inline-flex items-center justify-center w-16 h-16 ml-16 font-bold text-white rounded-full">!</span></div>
                                    </tooltip>
                                    <a v-else href="/checkout" class="btn btn-green btn-rounded w-full text-center">{{ translations.submit_button }}</a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mb-80 lg:mb-120 lg:mt-80 mt-40">
                        <slot></slot>
                    </div>
                </div>
                <div v-else key="empty" class="mb-160 lg:mb-200 lg:mt-60 mt-16 text-center">
                    <h1 class="h2 mb-16">{{ translations.shopping_basket_title }}</h1>
                    <p class="mb-40">{{ translations.empty_basket }}</p>
                    <a href="/producten" class="btn btn-rounded btn-lg btn-outline-blue">{{ translations.continue_shopping }}</a>
                </div>
            </transition-group>
        </div>
    </section>
</template>

<script>
import shoppingBasket from '@mixins/components/shopping-basket';

export default {
    name: 'Ui.ShoppingBasketFull',

    mixins: [shoppingBasket],

    data() {
        return {
            bannerOpen: true,
            ready: false,
        };
    },

    mounted() {
        this.$store.dispatch('syncBasketProducts').finally(() => {
            this.ready = true;
        });
    },
};
</script>
